import { useEffect, useMemo, useState } from "react";
import {
  useNumberingSettings,
  useUpdateNumberingSettings,
  type NumberingSettingsPatch,
  type NumberingSeries,
  type VerifactuQrMode,
} from "../api/hooks";
import { Button, Card, ErrorNotice, Field, Input, Modal, Notice, PageHeader, Select, Spinner } from "../components/ui";
import { formatInvoiceNumber, validateNextNumber, validateSeriesCode } from "../lib/erp-export";

/**
 * Ajustes de numeración de facturas. Solo super-admin.
 *
 * El número fiscal lo asigna un trabajo diario (04:20) a las facturas cobradas hace más de N días, en orden de
 * cobro. Aquí se ajustan la espera, las series (un dígito, como admite el ERP) y el PRÓXIMO número de cada serie,
 * por ejemplo para continuar desde el último que ya exista en el ERP.
 *
 * Una serie con facturas no se renombra: cambiar el dígito apunta a otra serie (se crea si no existe) con su
 * propio contador, y las facturas ya numeradas conservan su número.
 */

type Kind = "INVOICE" | "RECTIFICATIVE";

interface SeriesEditorProps {
  title: string;
  kind: Kind;
  idPrefix: string;
  series: NumberingSeries;
  year: number;
  codeDraft: string;
  numberDraft: string;
  onCode: (value: string) => void;
  onNumber: (value: string) => void;
}

function SeriesEditor({ title, kind, idPrefix, series, year, codeDraft, numberDraft, onCode, onNumber }: SeriesEditorProps) {
  const counter = series.counters.find((item) => item.fiscalYear === year);
  const lastNumber = counter?.lastNumber ?? 0;
  const min = counter?.minNextNumber ?? 1;
  const codeError = validateSeriesCode(codeDraft);
  const codeChanged = codeDraft.trim() !== series.code;
  const numberError = codeChanged ? null : validateNextNumber(numberDraft, min, series.maxNumber);

  return (
    <fieldset className="rounded border border-ink-200 p-4 dark:border-ink-700" id={`${idPrefix}-editor`}>
      <legend className="px-1 text-sm font-semibold text-ink-900 dark:text-ink-50">{title}</legend>
      <div className="flex flex-wrap gap-6">
        <div className="w-40">
          <Field label="Serie" htmlFor={`${idPrefix}-code`} hint="Un dígito, de 0 a 9." error={codeError ?? undefined}>
            <Input
              id={`${idPrefix}-code`}
              inputMode="numeric"
              maxLength={1}
              value={codeDraft}
              onChange={(event) => onCode(event.target.value)}
            />
          </Field>
        </div>
        <div className="w-56">
          <Field
            label={`Próximo número (${year})`}
            htmlFor={`${idPrefix}-next`}
            hint={
              codeChanged
                ? "Guarda primero el cambio de serie para ajustar su número."
                : `Última asignada: ${lastNumber === 0 ? "ninguna" : lastNumber}. Mínimo permitido: ${min}.`
            }
            error={numberError ?? undefined}
          >
            <Input
              id={`${idPrefix}-next`}
              inputMode="numeric"
              disabled={codeChanged}
              value={numberDraft}
              onChange={(event) => onNumber(event.target.value)}
            />
          </Field>
        </div>
      </div>
      {!codeChanged && !numberError && numberDraft.trim() !== "" ? (
        <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">
          La próxima {kind === "INVOICE" ? "factura" : "rectificativa"} saldrá como{" "}
          <strong>{formatInvoiceNumber(series.code, Number(numberDraft))}</strong>.
        </p>
      ) : null}
    </fieldset>
  );
}

export function NumberingSettingsPage() {
  const query = useNumberingSettings();
  const update = useUpdateNumberingSettings();
  const [days, setDays] = useState("7");
  const [testMode, setTestMode] = useState(false);
  const [qrMode, setQrMode] = useState<VerifactuQrMode>("TEST");
  const [year, setYear] = useState<number | null>(null);
  const [invoiceCode, setInvoiceCode] = useState("");
  const [rectCode, setRectCode] = useState("");
  const [invoiceNext, setInvoiceNext] = useState("");
  const [rectNext, setRectNext] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saved, setSaved] = useState(false);

  const data = query.data;
  const activeYear = year ?? data?.currentFiscalYear ?? new Date().getFullYear();

  // Años con contador (más el actual), para poder corregir también un ejercicio anterior.
  const years = useMemo(() => {
    const set = new Set<number>(data ? [data.currentFiscalYear] : []);
    for (const series of [data?.invoiceSeries, data?.rectificativeSeries]) {
      series?.counters.forEach((counter) => set.add(counter.fiscalYear));
    }
    return [...set].sort((a, b) => b - a);
  }, [data]);

  useEffect(() => {
    if (!data) return;
    setDays(String(data.numberingDelayDays));
    setTestMode(data.numberTestInvoices);
    setQrMode(data.verifactuQrMode);
    setInvoiceCode(data.invoiceSeries?.code ?? "");
    setRectCode(data.rectificativeSeries?.code ?? "");
  }, [data]);

  // El "próximo número" mostrado sigue al servidor y al año elegido.
  useEffect(() => {
    if (!data) return;
    const next = (series: NumberingSeries | null): string =>
      String(series?.counters.find((item) => item.fiscalYear === activeYear)?.nextNumber ?? 1);
    setInvoiceNext(next(data.invoiceSeries));
    setRectNext(next(data.rectificativeSeries));
  }, [data, activeYear]);

  const parsedDays = Number(days);
  const daysValid = days.trim() !== "" && Number.isInteger(parsedDays) && parsedDays >= 0 && parsedDays <= 90;

  const savedInvoice = data?.invoiceSeries ?? null;
  const savedRect = data?.rectificativeSeries ?? null;
  const invoiceCodeChanged = savedInvoice !== null && invoiceCode.trim() !== savedInvoice.code;
  const rectCodeChanged = savedRect !== null && rectCode.trim() !== savedRect.code;

  const counterOf = (series: NumberingSeries | null) => series?.counters.find((item) => item.fiscalYear === activeYear);
  const invoiceNextChanged = savedInvoice !== null && !invoiceCodeChanged && invoiceNext.trim() !== String(counterOf(savedInvoice)?.nextNumber ?? 1);
  const rectNextChanged = savedRect !== null && !rectCodeChanged && rectNext.trim() !== String(counterOf(savedRect)?.nextNumber ?? 1);

  const invoiceValid =
    validateSeriesCode(invoiceCode) === null &&
    (invoiceCodeChanged || !savedInvoice || validateNextNumber(invoiceNext, counterOf(savedInvoice)?.minNextNumber ?? 1, savedInvoice.maxNumber) === null);
  const rectValid =
    validateSeriesCode(rectCode) === null &&
    (rectCodeChanged || !savedRect || validateNextNumber(rectNext, counterOf(savedRect)?.minNextNumber ?? 1, savedRect.maxNumber) === null);
  const sameDigit = invoiceCode.trim() !== "" && invoiceCode.trim() === rectCode.trim();

  const basicChanged = data !== undefined && (parsedDays !== data.numberingDelayDays || testMode !== data.numberTestInvoices || qrMode !== data.verifactuQrMode);
  const seriesChanged = invoiceCodeChanged || rectCodeChanged || invoiceNextChanged || rectNextChanged;
  const dirty = basicChanged || seriesChanged;
  const canSave = dirty && daysValid && invoiceValid && rectValid && !sameDigit && !update.isPending;

  function buildPatch(): NumberingSettingsPatch {
    if (!data) return {};
    return {
      ...(parsedDays !== data.numberingDelayDays ? { numberingDelayDays: parsedDays } : {}),
      ...(testMode !== data.numberTestInvoices ? { numberTestInvoices: testMode } : {}),
      ...(qrMode !== data.verifactuQrMode ? { verifactuQrMode: qrMode } : {}),
      ...(invoiceCodeChanged ? { invoiceSeriesCode: invoiceCode.trim() } : {}),
      ...(rectCodeChanged ? { rectificativeSeriesCode: rectCode.trim() } : {}),
      // Una petición lleva un solo ajuste de número: si se tocaron los dos, el de facturas va primero y el otro
      // se guarda en una segunda llamada (ver handleSave).
      ...(invoiceNextChanged
        ? { nextNumber: { kind: "INVOICE" as const, fiscalYear: activeYear, number: Number(invoiceNext) } }
        : rectNextChanged
          ? { nextNumber: { kind: "RECTIFICATIVE" as const, fiscalYear: activeYear, number: Number(rectNext) } }
          : {}),
    };
  }

  async function handleSave(): Promise<void> {
    if (!data) return;
    setSaved(false);
    setConfirmOpen(false);
    const patch = buildPatch();
    const needsSecond = invoiceNextChanged && rectNextChanged;
    try {
      await update.mutateAsync(patch);
      if (needsSecond) {
        await update.mutateAsync({ nextNumber: { kind: "RECTIFICATIVE", fiscalYear: activeYear, number: Number(rectNext) } });
      }
      setSaved(true);
    } catch {
      // El error se muestra con `update.isError`.
    }
  }

  function requestSave(): void {
    // Las series y los números son delicados: se confirma. Los días de espera y el modo prueba se guardan directo.
    if (seriesChanged) setConfirmOpen(true);
    else void handleSave();
  }

  const changeLines: string[] = [];
  if (invoiceCodeChanged) changeLines.push(`Las facturas nuevas pasarán de la serie ${savedInvoice?.code} a la serie ${invoiceCode.trim()}.`);
  if (rectCodeChanged) changeLines.push(`Las rectificativas nuevas pasarán de la serie ${savedRect?.code} a la serie ${rectCode.trim()}.`);
  if (invoiceNextChanged) {
    changeLines.push(`Próxima factura (${activeYear}): ${formatInvoiceNumber(savedInvoice?.code ?? "", Number(invoiceNext))}.`);
  }
  if (rectNextChanged) {
    changeLines.push(`Próxima rectificativa (${activeYear}): ${formatInvoiceNumber(savedRect?.code ?? "", Number(rectNext))}.`);
  }

  return (
    <div>
      <PageHeader
        eyebrow="Facturación"
        title="Ajustes de numeración"
        description="Cuándo se asigna el número fiscal a las facturas cobradas, con qué serie y por qué número sigue. El orden siempre es el de la fecha de cobro."
      />

      {query.isLoading ? <Spinner /> : null}
      {query.isError ? <ErrorNotice error={query.error} onRetry={() => void query.refetch()} /> : null}

      {data && !data.configured ? (
        <Notice tone="danger">Faltan los ajustes de facturación (series de numeración). No se puede numerar hasta crearlos.</Notice>
      ) : null}

      {data?.configured && savedInvoice && savedRect ? (
        <Card>
          <div className="mb-4 max-w-xs">
            <Field label="Año fiscal" htmlFor="numbering-year" hint="Cada serie lleva un contador distinto por año.">
              <Select id="numbering-year" value={activeYear} onChange={(event) => setYear(Number(event.target.value))}>
                {years.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <div className="grid gap-4">
            <SeriesEditor
              title="Facturas"
              kind="INVOICE"
              idPrefix="invoice"
              series={savedInvoice}
              year={activeYear}
              codeDraft={invoiceCode}
              numberDraft={invoiceNext}
              onCode={(value) => {
                setSaved(false);
                setInvoiceCode(value);
              }}
              onNumber={(value) => {
                setSaved(false);
                setInvoiceNext(value);
              }}
            />
            <SeriesEditor
              title="Rectificativas"
              kind="RECTIFICATIVE"
              idPrefix="rectificative"
              series={savedRect}
              year={activeYear}
              codeDraft={rectCode}
              numberDraft={rectNext}
              onCode={(value) => {
                setSaved(false);
                setRectCode(value);
              }}
              onNumber={(value) => {
                setSaved(false);
                setRectNext(value);
              }}
            />
          </div>

          {sameDigit ? (
            <div className="mt-3">
              <Notice tone="danger">Facturas y rectificativas no pueden usar el mismo dígito de serie.</Notice>
            </div>
          ) : null}
          <p className="mt-3 text-xs text-ink-500 dark:text-ink-400">
            Una serie con facturas no se renombra: al cambiar el dígito las nuevas usan otra serie (con su propio contador) y las ya
            numeradas conservan su número. El próximo número nunca puede quedar por debajo del último ya asignado, para no repetir
            números.
          </p>

          <hr className="my-5 border-ink-200 dark:border-ink-700" />

          <div className="max-w-xs">
            <Field
              label="Días de espera tras el cobro"
              htmlFor="numbering-delay"
              hint="Margen para que una anulación o un reembolso llegue antes de que la factura consuma un número. De 0 a 90."
              error={daysValid ? undefined : "Entre 0 y 90, número entero"}
            >
              <Input
                id="numbering-delay"
                type="number"
                min={0}
                max={90}
                value={days}
                onChange={(event) => {
                  setSaved(false);
                  setDays(event.target.value);
                }}
              />
            </Field>
          </div>

          <label className="mt-4 flex items-start gap-2 text-sm text-ink-800 dark:text-ink-100">
            <input
              type="checkbox"
              className="mt-1"
              checked={testMode}
              onChange={(event) => {
                setSaved(false);
                setTestMode(event.target.checked);
              }}
            />
            <span>
              Numerar también las facturas del modo prueba de Stripe
              <span className="block text-xs text-ink-500 dark:text-ink-400">
                Solo para probar con una empresa de pruebas del ERP. Desactívalo en producción: consumiría números de la serie real.
              </span>
            </span>
          </label>

          {testMode ? (
            <div className="mt-3">
              <Notice tone="warn">Con esto activo, las facturas de prueba de Stripe consumen números de la serie real.</Notice>
            </div>
          ) : null}

          <hr className="my-5 border-ink-200 dark:border-ink-700" />

          <div className="max-w-md">
            <Field
              label="QR de VERI*FACTU en la factura"
              htmlFor="numbering-verifactu-qr"
              hint="Se imprime entre el logo y el título con los datos de la factura (NIF del emisor, número, fecha e importe). Solo afecta a las facturas que se generen a partir de ahora."
            >
              <Select
                id="numbering-verifactu-qr"
                value={qrMode}
                onChange={(event) => {
                  setSaved(false);
                  setQrMode(event.target.value as VerifactuQrMode);
                }}
              >
                <option value="TEST">Servicio de pruebas de la AEAT (hasta 2028)</option>
                <option value="PRODUCTION">Servicio real de la AEAT</option>
                <option value="OFF">Sin QR</option>
              </Select>
            </Field>
          </div>
          {qrMode === "PRODUCTION" ? (
            <div className="mt-3">
              <Notice tone="warn">
                Con el servicio real, el QR de una factura que no esté registrada en la AEAT dará «no encontrada» al escanearlo.
              </Notice>
            </div>
          ) : null}

          {update.isError ? (
            <div className="mt-3">
              <ErrorNotice error={update.error} />
            </div>
          ) : null}
          {saved && !dirty ? (
            <div className="mt-3">
              <Notice tone="ok">Ajustes guardados.</Notice>
            </div>
          ) : null}

          <div className="mt-4">
            <Button variant="primary" disabled={!canSave} onClick={requestSave}>
              {update.isPending ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        </Card>
      ) : null}

      <Modal open={confirmOpen} title="Confirmar cambios de numeración" onClose={() => setConfirmOpen(false)}>
        <ul className="list-disc space-y-1 pl-5 text-sm text-ink-800 dark:text-ink-100">
          {changeLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <p className="mt-3 text-sm text-ink-600 dark:text-ink-300">
          Comprueba que coincide con lo último que tienes en el ERP: un número repetido allí no se puede deshacer desde aquí.
        </p>
        <div className="mt-4 flex gap-3">
          <Button variant="primary" onClick={() => void handleSave()} disabled={update.isPending}>
            Confirmar y guardar
          </Button>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
        </div>
      </Modal>
    </div>
  );
}
